import { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import UserContext from "context/UserContext";
import { getOfficialCategoryDiagnosticsRoute, postOrganizationEditRoute } from "network/api-routes";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import performRequest from "network/perform-request";

const MAX_IMAGE_SIZE_MB = 1;

const FIELD_OPTIONS = [
  { key: "name", label: "Nombre y apellido" },
  { key: "document_number", label: "Nº de documento" },
  { key: "cellphone_number", label: "Teléfono" },
  { key: "email", label: "Email" },
];

const getImageUrl = (organization, keys) => keys.map((key) => organization?.[key]).find(Boolean) ?? null;

const createSettings = (organization) => ({
  organization_id: organization?.id,
  name: organization?.name ?? "",
  description: organization?.description ?? "",
  requires_membership_to_join_tournaments: organization?.requires_membership_to_join_tournaments ?? false,
  enforces_official_category_on_signup: organization?.enforces_official_category_on_signup ?? false,
  non_members_can_play_but_not_score: organization?.non_members_can_play_but_not_score ?? false,
  generates_member_qr_codes: organization?.generates_member_qr_codes ?? false,
});

const createMediaState = (organization) => ({
  banner: null,
  bannerUrl: getImageUrl(organization, ["full_banner_url", "banner_url"]),
  logo: null,
  logoUrl: getImageUrl(organization, ["full_logo_url", "logo_url"]),
});

const SectionCard = ({ title, subtitle, children, action }) => (
  <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
    <Stack spacing={2.5}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems="start">
        <Box>
          <Typography variant="h5">{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </Stack>
  </Paper>
);

const ImageFieldCard = ({
  title,
  description,
  preview,
  inputName,
  onChange,
  aspectRatio,
  isLogo = false,
  recommendedSize,
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      height: "100%",
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "background.paper",
    }}
  >
    <Stack spacing={2} height="100%">
      <Box>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: isLogo ? 180 : 160,
          borderRadius: 3,
          border: "1px dashed",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.04) 0%, rgba(15, 23, 42, 0.08) 50%, rgba(15, 23, 42, 0.02) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          aspectRatio,
        }}
      >
        {preview ? (
          isLogo ? (
            <Avatar src={preview} alt={title} sx={{ width: 110, height: 110, bgcolor: "grey.100" }} />
          ) : (
            <Box
              component="img"
              src={preview}
              alt={title}
              sx={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          )
        ) : (
          <Stack spacing={1} alignItems="center" sx={{ px: 2, textAlign: "center", color: "text.secondary" }}>
            <AddPhotoAlternateOutlinedIcon />
            <Typography variant="body2">Todavía no hay una imagen cargada.</Typography>
          </Stack>
        )}
      </Box>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
      >
        <Typography variant="caption" color="text.secondary">
          Recomendado: {recommendedSize}. Máximo {MAX_IMAGE_SIZE_MB} MB.
        </Typography>
        <Button variant="outlined" component="label" size="small">
          Cambiar
          <input type="file" hidden accept="image/*" name={inputName} onChange={onChange} />
        </Button>
      </Stack>
    </Stack>
  </Paper>
);

export default function OrganizationSettingsForm() {
  const { user, selectedOrganization, setSelectedOrganization } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(() => createSettings(selectedOrganization));
  const [requiredFields, setRequiredFields] = useState(() => {
    const initialFields = selectedOrganization?.required_registration_fields;
    return Array.isArray(initialFields) ? initialFields : [];
  });
  const [media, setMedia] = useState(() => createMediaState(selectedOrganization));
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);

  useEffect(() => {
    setSettings(createSettings(selectedOrganization));
    const initialFields = selectedOrganization?.required_registration_fields;
    setRequiredFields(Array.isArray(initialFields) ? initialFields : []);
    setMedia(createMediaState(selectedOrganization));
  }, [selectedOrganization]);

  const organizationTitle = settings.name || selectedOrganization?.name || "Organización";

  const imageStatus = useMemo(
    () => [
      { label: media.logoUrl ? "Logo listo" : "Sin logo", color: media.logoUrl ? "success" : "default" },
      { label: media.bannerUrl ? "Banner listo" : "Sin banner", color: media.bannerUrl ? "success" : "default" },
    ],
    [media.bannerUrl, media.logoUrl],
  );

  const handleChange = (field) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCheck = (field) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [field]: event.target.checked,
      ...(field === "requires_membership_to_join_tournaments" && !event.target.checked
        ? { enforces_official_category_on_signup: false }
        : {}),
    }));
  };

  const requestOfficialCategoryEnable = () => {
    performRequest("GET", getOfficialCategoryDiagnosticsRoute(selectedOrganization.id), {}, true, user)
      .then((response) => {
        setDiagnostics(response.data);
        setDiagnosticsOpen(true);
      })
      .catch((error) => enqueueSnackbar(error?.response?.data?.message || "No se pudo revisar la configuración de categorías", { variant: "error" }));
  };

  const handleToggleRequired = (fieldKey) => (event) => {
    const checked = event.target.checked;
    setRequiredFields((prev) => {
      const nextFields = new Set(prev);
      if (checked) {
        nextFields.add(fieldKey);
      } else {
        nextFields.delete(fieldKey);
      }
      return Array.from(nextFields);
    });
  };

  const handleMediaChange = (type) => (event) => {
    const uploadedFile = event.target.files?.[0];

    if (!uploadedFile) {
      return;
    }

    const fileSizeMb = uploadedFile.size / 1024 / 1024;
    if (fileSizeMb > MAX_IMAGE_SIZE_MB) {
      enqueueSnackbar("El archivo es demasiado grande", { variant: "error" });
      return;
    }

    setMedia((prev) => ({
      ...prev,
      [type]: uploadedFile,
      [`${type}Url`]: URL.createObjectURL(uploadedFile),
    }));
  };

  const isRequired = (key) => requiredFields.includes(key);

  const handleSave = () => {
    setSaving(true);

    const headers = { "Content-Type": "multipart/form-data" };
    const formData = new FormData();

    Object.entries(settings).forEach(([key, value]) => {
      formData.append(key, value);
    });

    formData.append("required_registration_fields", JSON.stringify(requiredFields ?? []));

    if (media.banner) {
      formData.append("banner", media.banner, `${organizationTitle}-banner.jpg`);
    }

    if (media.logo) {
      formData.append("logo", media.logo, `${organizationTitle}-logo.jpg`);
    }

    performRequest("POST", postOrganizationEditRoute, formData, true, user, null, headers)
      .then((response) => {
        const updatedOrg = response.data.organization;
        setSelectedOrganization(updatedOrg);
        enqueueSnackbar(intl.formatMessage({ id: "Los cambios se guardaron correctamente" }), {
          variant: "success",
        });
      })
      .catch((error) => {
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "No se pudieron guardar los cambios" }),
          {
            variant: "error",
          },
        );
        console.log(error);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Stack spacing={2.5} sx={{ pb: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 4,
          color: "common.white",
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ xs: "flex-start", md: "center" }}>
          <Avatar
            src={media.logoUrl}
            alt={organizationTitle}
            sx={{ width: 84, height: 84, bgcolor: "rgba(255,255,255,0.18)" }}
          >
            {organizationTitle.charAt(0).toUpperCase()}
          </Avatar>

          <Stack spacing={1} flex={1}>
            <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 1.2 }}>
              Configuración de la organización
            </Typography>
            <Typography variant="h4">{organizationTitle}</Typography>
            <Typography variant="body1" sx={{ opacity: 0.88, maxWidth: 760 }}>
              Actualizá la identidad visual, las reglas de inscripción y las automatizaciones que querés aplicar a los
              miembros y torneos de la organización.
            </Typography>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {imageStatus.map((item) => (
                <Chip
                  key={item.label}
                  label={item.label}
                  color={item.color}
                  variant={item.color === "success" ? "filled" : "outlined"}
                  sx={{ bgcolor: item.color === "success" ? undefined : "rgba(255,255,255,0.08)", color: "inherit" }}
                />
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <SectionCard
        title="Información general"
        subtitle="Estos datos se usan como presentación principal de la organización dentro de la plataforma."
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nombre de la organización"
              value={settings.name}
              onChange={handleChange("name")}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descripción"
              value={settings.description}
              onChange={handleChange("description")}
              fullWidth
              multiline
              rows={4}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        title="Identidad visual"
        subtitle="Podés administrar por separado el logo y el banner para que cada superficie tenga su propia imagen."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ImageFieldCard
              title="Logo"
              description="Se usa en vistas compactas, perfiles y piezas de marca."
              preview={media.logoUrl}
              inputName="logo"
              onChange={handleMediaChange("logo")}
              aspectRatio="16 / 9"
              isLogo
              recommendedSize="512 x 512 px"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ImageFieldCard
              title="Banner"
              description="Ideal para cabeceras horizontales y espacios promocionales."
              preview={media.bannerUrl}
              inputName="banner"
              onChange={handleMediaChange("banner")}
              aspectRatio="16 / 9"
              recommendedSize="1600 x 900 px"
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        title="Reglas y automatizaciones"
        subtitle="Definí cómo se comporta la organización cuando los jugadores se inscriben o cuando se crean miembros nuevos."
        action={
          <Chip
            icon={<QrCode2OutlinedIcon />}
            label={settings.generates_member_qr_codes ? "QR activo" : "QR inactivo"}
            color={settings.generates_member_qr_codes ? "success" : "default"}
            variant={settings.generates_member_qr_codes ? "filled" : "outlined"}
          />
        }
      >
        <Stack spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.generates_member_qr_codes}
                    onChange={handleCheck("generates_member_qr_codes")}
                  />
                }
                label="Generar códigos QR automáticamente a cada miembro"
              />
              <Typography variant="body2" color="text.secondary" sx={{ pl: { sm: 6 } }}>
                Si está activo, se generará un QR tanto al crear miembros manualmente como al importarlos, usando el
                valor de `generates_member_qr_codes` de la organización.
              </Typography>
            </Stack>
          </Paper>

          <FormControlLabel
            control={
              <Checkbox
                checked={settings.requires_membership_to_join_tournaments}
                onChange={handleCheck("requires_membership_to_join_tournaments")}
              />
            }
            label="Requiere membresía para inscribirse a torneos"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={settings.non_members_can_play_but_not_score}
                onChange={handleCheck("non_members_can_play_but_not_score")}
                disabled={settings.requires_membership_to_join_tournaments}
              />
            }
            label="Permitir que jugadores no miembros jueguen, pero sin puntuar"
          />

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <FormControlLabel
              control={<Switch checked={settings.enforces_official_category_on_signup} disabled={!settings.requires_membership_to_join_tournaments} onChange={(event) => {
                if (event.target.checked) requestOfficialCategoryEnable();
                else setSettings((prev) => ({ ...prev, enforces_official_category_on_signup: false }));
              }} />}
              label="Exigir categoría oficial al inscribirse"
            />
            <Typography variant="body2" color="text.secondary" sx={{ pl: { sm: 6 } }}>
              Se revisará el catálogo, rankings y temporadas vigentes antes de activar esta regla. Los miembros sin categoría o fecha pueden ser rechazados según el torneo.
            </Typography>
          </Paper>
        </Stack>
      </SectionCard>

      <SectionCard
        title="Requisitos de inscripción"
        subtitle="Elegí qué datos serán obligatorios para que los jugadores puedan inscribirse a torneos de esta organización."
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Si el jugador ya tiene Reva ID verificado, estos campos se consideran cumplidos automáticamente.
          </Typography>

          <Divider />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1,
            }}
          >
            {FIELD_OPTIONS.map((option) => (
              <Tooltip key={option.key} title="">
                <FormControlLabel
                  control={<Checkbox checked={isRequired(option.key)} onChange={handleToggleRequired(option.key)} />}
                  label={option.label}
                />
              </Tooltip>
            ))}
          </Box>

          <Alert severity="info" icon={false}>
            Se aplica a todos los torneos de esta organización.
          </Alert>
        </Stack>
      </SectionCard>

      <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ alignSelf: "flex-end", px: 3 }}>
        {saving ? "Guardando..." : "Guardar cambios"}
      </Button>
      <Dialog open={diagnosticsOpen} onClose={() => setDiagnosticsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Revisar categoría oficial</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} pt={1}>
            {(diagnostics?.blockers || []).map((item) => <Alert key={item.code} severity="error">{item.message}</Alert>)}
            {(diagnostics?.warnings || []).map((item) => <Alert key={item.code} severity="warning">{item.message} {item.count ? `(${item.count})` : ""}</Alert>)}
            {!diagnostics?.blockers?.length && <Alert severity="success">La organización puede activar la regla.</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setDiagnosticsOpen(false)}>Cancelar</Button><Button variant="contained" disabled={!diagnostics?.can_enable} onClick={() => { setSettings((prev) => ({ ...prev, enforces_official_category_on_signup: true })); setDiagnosticsOpen(false); }}>Confirmar activación</Button></DialogActions>
      </Dialog>
    </Stack>
  );
}
